import { ApolloQueryResult, useApolloClient } from "@apollo/client";
import {
  CompetencyMapDocument,
  CompetencyMapQuery,
  CompetencyMapQuestionsQuery,
  Exact,
  Scalars,
  useConfirmOrRejectQuestionsMutation,
  useDeleteQuestionsMutation,
} from "@generated/graphql";
import { CheckOutlined, CloseOutlined, DeleteOutline, PlayArrowOutlined } from "@mui/icons-material";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { parseApolloError } from "helpers/errorsParser";
import { formatNumber } from "helpers/index";
import useCreateTesting from "hooks/useCreateTesting";
import { useContext, useMemo, useState } from "react";
import SelectionActionBar from "../../components/Selection/SelectionActionBar";
import { ConstructorTabContext } from "../../context";
import { CompetencyMapQuestion } from "./types";
import { getQuestionSelectionStats } from "./utils";

interface QuestionSelectionActionsProps {
  competencyMap: CompetencyMapQuery["competencyMap"];
  questions: CompetencyMapQuestion[];
  refetchCompetencyMapQuestions: (
    variables?:
      | Partial<
          Exact<{
            competencyMapId: Scalars["ID"]["input"];
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<CompetencyMapQuestionsQuery>>;
}

const QuestionSelectionActions = ({
  competencyMap,
  questions,
  refetchCompetencyMapQuestions,
}: QuestionSelectionActionsProps) => {
  const client = useApolloClient();
  const { handleOpen } = useContext(ConfirmModalContext);
  const { addToast } = useContext(ToastContext);
  const { selectionScope, selectedEntities, onClearSelectionMode } = useContext(ConstructorTabContext);
  const { createTesting } = useCreateTesting({ competencyMap });
  const [activeAction, setActiveAction] = useState<"confirm" | "reject" | "delete" | null>(null);
  const [confirmOrRejectQuestions] = useConfirmOrRejectQuestionsMutation();
  const [deleteQuestions] = useDeleteQuestionsMutation();

  const stats = useMemo(
    () => getQuestionSelectionStats(selectedEntities.questionIds, questions),
    [questions, selectedEntities.questionIds]
  );
  const selectedQuestionIds = stats.selectedQuestions.map((question) => question.id);
  const confirmedQuestionIds = stats.confirmedQuestions.map((question) => question.id);
  const unconfirmedQuestionIds = stats.unconfirmedQuestions.map((question) => question.id);
  const isOpen = selectionScope === "questions";

  const runMutationWithRefetch = async (callback: () => Promise<void>, successText: string) => {
    try {
      await callback();
      await Promise.all([refetchCompetencyMapQuestions(), client.refetchQueries({ include: [CompetencyMapDocument] })]);
      addToast({ type: ToastTypeEnum.SUCCESS, text: successText });
      onClearSelectionMode();
    } catch (error) {
      parseApolloError(error, { addToast });
    }
  };

  const openConfirmModal = ({
    action,
    title,
    actionButtonText,
    description,
    questionIds,
    successText,
    mutation,
  }: {
    action: "confirm" | "reject" | "delete";
    title: string;
    actionButtonText: string;
    description: string;
    questionIds: string[];
    successText: string;
    mutation: (questionIds: string[]) => Promise<unknown>;
  }) => {
    handleOpen({
      title,
      description,
      actionButtonText,
      loading: activeAction === action,
      handleClick: async () => {
        setActiveAction(action);

        await runMutationWithRefetch(async () => {
          await mutation(questionIds);
        }, successText);

        setActiveAction(null);
      },
    });
  };

  let confirmTooltip = "Подтвердить выбранные вопросы";
  if (stats.unconfirmedCount === 0) {
    confirmTooltip = "Все выбранные вопросы уже подтверждены";
  } else if (stats.unconfirmedCount < stats.total) {
    confirmTooltip = [
      `Будет подтверждено ${stats.unconfirmedCount} из ${stats.total} вопросов,`,
      "уже подтвержденные не затронуты",
    ].join(" ");
  }

  let rejectTooltip = "Отклонить выбранные вопросы";
  if (stats.confirmedCount === 0) {
    rejectTooltip = "Все выбранные вопросы уже не подтверждены";
  } else if (stats.confirmedCount < stats.total) {
    rejectTooltip = [
      `Будет отклонено ${stats.confirmedCount} из ${stats.total} вопросов,`,
      "уже неподтвержденные не затронуты",
    ].join(" ");
  }

  let createTestingTooltip = "Запустить тест с выбранными вопросами";
  if (stats.confirmedCount === 0) {
    createTestingTooltip = "Нет подтвержденных вопросов - тест нельзя запустить";
  } else if (stats.confirmedCount < stats.total) {
    createTestingTooltip = `Тест будет создан только из ${stats.confirmedCount} подтвержденных вопросов`;
  }

  const deleteTooltip =
    stats.confirmedCount > 0
      ? ["Среди выбранных есть подтвержденные вопросы.", "Сначала отклоните их, затем удалите"].join(" ")
      : "Удалить выбранные вопросы из карты";

  const handleConfirm = () => {
    const description =
      stats.unconfirmedCount < stats.total
        ? [
            `Будет подтверждено ${stats.unconfirmedCount} из ${stats.total} выбранных вопросов.`,
            "Уже подтвержденные затронуты не будут.",
          ].join(" ")
        : `Будет подтверждено ${stats.total} вопросов.`;

    openConfirmModal({
      action: "confirm",
      title: "Подтвердить вопросы?",
      actionButtonText: "Подтвердить",
      description,
      questionIds: unconfirmedQuestionIds,
      successText: "Вопросы подтверждены",
      mutation: (questionIds) => confirmOrRejectQuestions({ variables: { questionIds } }),
    });
  };

  const handleReject = () => {
    const description =
      stats.confirmedCount < stats.total
        ? [
            `Будет отклонено ${stats.confirmedCount} из ${stats.total} выбранных вопросов.`,
            "Уже неподтвержденные не затронуты.",
          ].join(" ")
        : `Будет отклонено ${stats.total} вопросов.`;

    openConfirmModal({
      action: "reject",
      title: "Отклонить вопросы?",
      actionButtonText: "Отклонить",
      description,
      questionIds: confirmedQuestionIds,
      successText: "Вопросы отклонены",
      mutation: (questionIds) => confirmOrRejectQuestions({ variables: { questionIds } }),
    });
  };

  const handleCreateTesting = () => {
    const selectParts: CompetencyMapSelectedParts = {
      questionIds: confirmedQuestionIds,
    };

    createTesting(selectParts);
  };

  const handleDelete = () => {
    openConfirmModal({
      action: "delete",
      title: "Удалить вопросы?",
      actionButtonText: "Удалить",
      description: `Будет удалено ${stats.total} выбранных вопросов из карты. Действие необратимо.`,
      questionIds: selectedQuestionIds,
      successText: "Вопросы удалены",
      mutation: (questionIds) => deleteQuestions({ variables: { questionIds } }),
    });
  };

  return (
    <SelectionActionBar
      open={isOpen}
      selectionLabel={formatNumber(["вопрос", "вопроса", "вопросов"], stats.total)}
      actions={[
        {
          id: "confirm",
          title: "Подтвердить",
          tooltip: confirmTooltip,
          onClick: handleConfirm,
          disabled: stats.unconfirmedCount === 0,
          loading: activeAction === "confirm",
          color: "success",
          startIcon: <CheckOutlined />,
        },
        {
          id: "reject",
          title: "Отклонить",
          tooltip: rejectTooltip,
          onClick: handleReject,
          disabled: stats.confirmedCount === 0,
          loading: activeAction === "reject",
          color: "warning",
          startIcon: <CloseOutlined />,
        },
        {
          id: "testing",
          title: "Создать тестирование",
          tooltip: createTestingTooltip,
          onClick: handleCreateTesting,
          disabled: stats.confirmedCount === 0,
          startIcon: <PlayArrowOutlined />,
        },
        {
          id: "delete",
          title: "Удалить",
          tooltip: deleteTooltip,
          onClick: handleDelete,
          disabled: stats.confirmedCount > 0,
          loading: activeAction === "delete",
          color: "error",
          startIcon: <DeleteOutline />,
        },
      ]}
      onClose={onClearSelectionMode}
    />
  );
};

export default QuestionSelectionActions;
